/**
 * TeamMemberDetailDrawer — 80%-width right-side blade for team member detail.
 *
 * Mirrors ClientDetailDrawer's shell: slide-in right Overlay+Panel,
 * sticky header, stacked sections, Escape/backdrop close.
 *
 * Widths:
 *   desktop  (>1024px) : min(80vw, 1280px)
 *   landscape tablet (≤1024px) : 90vw
 *   portrait tablet / mobile (≤768px) : 100vw
 *
 * Nested overlay stacking:
 *   The Panel sits at theme.zIndex.modal (1050).
 *   EditMemberModal is passed zIndex={theme.zIndex.popover} (1060) so it
 *   stacks above the drawer. The Escape useEffect depends on [showEdit, onClose]
 *   and returns early when showEdit is true, suppressing drawer-close while the
 *   modal is open without needing a ref.
 *
 * Permission gating:
 *   The Edit button and the status actions (Deactivate / Reactivate) require the
 *   org-admin-only 'team:manage:actions' permission — deactivation disables the
 *   member's Entra sign-in, so it sits on the same tier as the departure wizard
 *   (POST /members/:id/depart) rather than on the read/browse 'team:manage' tier.
 *   'Activate Login' deliberately stays on 'team:manage'.
 *   Checked via useAuthStore().hasPermission(...).
 */

import { useEffect, useState } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Pencil } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { WarningToast, useAutoDismissWarning } from '../../components/ui/WarningToast';
import { TempPasswordModal } from '../../components/ui/TempPasswordModal';
import { media } from '../../styles/media';
import { useAuthStore } from '../../stores/authStore';
import userService, { Member } from '../../services/api/userService';
import memberService from '../../services/api/memberService';
import { EditMemberModal } from './EditMemberModal';
import { ReactivateMemberModal } from './ReactivateMemberModal';
import { extractApiError } from '../../utils/apiError';

// ==================== Types ====================

interface TeamMemberDetailDrawerProps {
  member: Member | null;
  onClose: () => void;
}

// ==================== Helpers ====================

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

// ==================== Animations ====================

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

// ==================== Styled Components ====================

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(80vw, 1280px);
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;

  ${media.desktop} {
    width: 90vw;
  }

  ${media.tablet} {
    width: 100vw;
  }
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  z-index: 1;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const TitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
  min-width: 0;
`;

const DrawerTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MemberEmail = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing[8]};
    padding-top: ${({ theme }) => theme.spacing[6]};
    border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const SectionHeading = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
`;

const FieldGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  margin: 0;

  ${media.tablet} {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.dt`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const FieldValue = styled.dd`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
`;

const NoneText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GroupChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const GroupChip = styled.span<{ $index: number }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  ${({ $index, theme }) => {
    const palettes = [
      `background: ${theme.colors.status.info.bg}; color: ${theme.colors.status.info.text}; border: 1px solid ${theme.colors.status.info.border};`,
      `background: ${theme.colors.status.locked.bg}; color: ${theme.colors.status.locked.text}; border: 1px solid ${theme.colors.status.locked.border};`,
      `background: ${theme.colors.status.warning.bg}; color: ${theme.colors.status.warning.text}; border: 1px solid ${theme.colors.status.warning.border};`,
      `background: ${theme.colors.status.success.bg}; color: ${theme.colors.status.success.text}; border: 1px solid ${theme.colors.status.success.border};`,
    ];
    return palettes[$index % palettes.length];
  }}
`;

const StatusCard = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.status.success.bg : theme.colors.status.error.bg};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.status.success.border : theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  flex-wrap: wrap;
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StatusDot = styled.span<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.status.success.text : theme.colors.status.error.text};
  flex-shrink: 0;
`;

const StatusText = styled.span<{ $active: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.status.success.text : theme.colors.status.error.text};
`;

const ClientsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-width: 400px;
`;

const ClientsTableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ClientsTh = styled.th`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.page};
`;

const ClientsTd = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};

  tr:last-child & {
    border-bottom: none;
  }
`;

// ==================== Component ====================

export function TeamMemberDetailDrawer({
  member: memberProp,
  onClose,
}: TeamMemberDetailDrawerProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  // Re-fetch the member by id so edits made from inside the drawer (which invalidate
  // ['members']) refresh here instead of leaving the passed-in snapshot stale. The
  // snapshot seeds initialData so there's no loading flash.
  const { data: freshMember } = useQuery({
    queryKey: ['members', 'detail', memberProp?.id],
    queryFn: () => userService.getMemberById(memberProp!.id),
    enabled: !!memberProp?.id,
    initialData: memberProp ?? undefined,
  });
  const member = freshMember ?? memberProp;

  const [showEdit, setShowEdit] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);

  const canManage = hasPermission('team:manage');
  // Edit member (roles, groups, supervisor, location) is gated by the org-admin-only
  // team:manage:actions permission; lifecycle actions (activate/deactivate) stay on team:manage.
  const canEditMember = hasPermission('team:manage:actions');
  // z-index for EditMemberModal — must exceed the drawer Panel (modal=1050)
  const nestedZIndex = theme.zIndex.popover; // 1060

  const { warning: successMsg, showWarning: showSuccess, clear: clearSuccess } = useAutoDismissWarning('Done', 5000);
  const { warning: errorMsg, showWarning: showError, clear: clearError } = useAutoDismissWarning('Failed. Please try again.');

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => memberService.deactivateMember(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
    onError: (err) => showError(extractApiError(err, 'Failed to deactivate member. Please try again.')),
  });

  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [tempPasswordLabel, setTempPasswordLabel] = useState('');

  const activateLoginMutation = useMutation({
    mutationFn: (id: string) => memberService.activateLogin(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      if (result.temporaryPassword) {
        setTempPassword(result.temporaryPassword);
        setTempPasswordLabel(`${member?.firstName} ${member?.lastName} (${member?.email})`);
        if (result.entraProvisioned === false) {
          // Password was generated but Entra provisioning failed — it won't work to
          // sign in yet. Surface this alongside the password so it isn't shared as-is.
          showError(
            'Login provisioning is pending — this password will not work until it is retried via Reset Password.',
          );
        }
      } else {
        showSuccess(`Login activated. ${member?.firstName} will receive a welcome email.`);
      }
    },
    onError: () => showError(undefined),
  });

  const handleTempPasswordClose = () => {
    setTempPassword(null);
    setTempPasswordLabel('');
  };

  // Close on Escape — suppressed while EditMemberModal / ReactivateMemberModal is open.
  // Re-registers when showEdit toggles (two extra listener calls per open/close cycle,
  // negligible) — avoids keeping a ref just for this.
  const nestedOpen = showEdit || showReactivate;
  useEffect(() => {
    if (nestedOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [nestedOpen, onClose]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // The parent conditionally renders this component only when member !== null,
  // but the prop type includes null to match useState's initial value. The guard
  // below satisfies TypeScript; in practice it never triggers.
  if (!member) return null;

  const isActive = member.status === 'active';
  const supervisorName = member.supervisorName ?? null;
  const assignedClients = member.assignedClients ?? [];

  return (
    <>
      <WarningToast message={successMsg} onDismiss={clearSuccess} variant="success" zIndex={theme.zIndex.tooltip} />
      <WarningToast message={errorMsg} onDismiss={clearError} variant="error" zIndex={theme.zIndex.tooltip} />
      <Overlay onClick={onClose}>
        <Panel
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Team member detail: ${member.firstName} ${member.lastName}`}
        >
          {/* ---- Sticky Header ---- */}
          <StickyHeader>
            <HeaderRow>
              <TitleArea>
                <DrawerTitle>
                  {member.firstName} {member.lastName}
                </DrawerTitle>
                <MemberEmail>{member.email}</MemberEmail>
                <Badge variant={isActive ? 'success' : 'error'}>
                  {isActive ? 'Active' : 'Inactive'}
                </Badge>
              </TitleArea>

              <HeaderActions>
                {canEditMember && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Pencil size={14} />}
                    onClick={() => setShowEdit(true)}
                  >
                    Edit
                  </Button>
                )}
                <CloseBtn onClick={onClose} aria-label="Close member detail">
                  <X />
                </CloseBtn>
              </HeaderActions>
            </HeaderRow>
          </StickyHeader>

          {/* ---- Body ---- */}
          <Body>
            {/* PROFILE SECTION */}
            <Section>
              <SectionHeading>Profile</SectionHeading>
              <FieldGrid>
                <div>
                  <FieldLabel>Role</FieldLabel>
                  <FieldValue>
                    {member.roles && member.roles.length > 0 ? (
                      <Badge roleName={member.roles[0].name}>{member.roles[0].name}</Badge>
                    ) : (
                      <NoneText>No role assigned</NoneText>
                    )}
                  </FieldValue>
                </div>

                <div>
                  <FieldLabel>Location</FieldLabel>
                  <FieldValue>
                    {member.primaryCenter?.name ? (
                      member.primaryCenter.name
                    ) : (
                      <NoneText>None</NoneText>
                    )}
                  </FieldValue>
                </div>

                <div>
                  <FieldLabel>Supervisor</FieldLabel>
                  <FieldValue>
                    {supervisorName ? supervisorName : <NoneText>None</NoneText>}
                  </FieldValue>
                </div>

                <div>
                  <FieldLabel>Member since</FieldLabel>
                  <FieldValue>{formatDate(member.createdAt)}</FieldValue>
                </div>
              </FieldGrid>
            </Section>

            {/* GROUPS SECTION */}
            <Section>
              <SectionHeading>Groups</SectionHeading>
              {member.groups && member.groups.length > 0 ? (
                <GroupChips>
                  {member.groups.map((g, idx) => (
                    <GroupChip key={g.id} $index={idx}>
                      {g.name}
                    </GroupChip>
                  ))}
                </GroupChips>
              ) : (
                <NoneText>No groups assigned</NoneText>
              )}
            </Section>

            {/* ASSIGNED CLIENTS SECTION — only when data exists */}
            {assignedClients.length > 0 && (
              <Section>
                <SectionHeading>Assigned Clients</SectionHeading>
                <ClientsTableWrapper>
                  <ClientsTable>
                    <thead>
                      <tr>
                        <ClientsTh>Client</ClientsTh>
                        <ClientsTh>Location</ClientsTh>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedClients.map((c) => (
                        <tr key={c.id}>
                          <ClientsTd>{c.name}</ClientsTd>
                          <ClientsTd>{c.location ?? '—'}</ClientsTd>
                        </tr>
                      ))}
                    </tbody>
                  </ClientsTable>
                </ClientsTableWrapper>
              </Section>
            )}

            {/* LOGIN ACCESS SECTION */}
            <Section>
              <SectionHeading>Login Access</SectionHeading>
              <StatusCard $active={!!member.loginActivated}>
                <StatusInfo>
                  <StatusDot $active={!!member.loginActivated} />
                  <StatusText $active={!!member.loginActivated}>
                    {member.loginActivated ? 'Login activated' : 'Login not activated'}
                  </StatusText>
                </StatusInfo>
                {canManage && !member.loginActivated && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => activateLoginMutation.mutate(member.id)}
                    isLoading={activateLoginMutation.isPending}
                  >
                    Activate Login
                  </Button>
                )}
              </StatusCard>
            </Section>

            {/* STATUS SECTION */}
            <Section>
              <SectionHeading>Status</SectionHeading>
              <StatusCard $active={isActive}>
                <StatusInfo>
                  <StatusDot $active={isActive} />
                  <StatusText $active={isActive}>
                    {isActive ? `Active since ${formatDate(member.createdAt)}` : 'Inactive'}
                  </StatusText>
                </StatusInfo>
                {canEditMember &&
                  (isActive ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => deactivateMutation.mutate(member.id)}
                      isLoading={deactivateMutation.isPending}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button variant="primary" size="sm" onClick={() => setShowReactivate(true)}>
                      Reactivate
                    </Button>
                  ))}
              </StatusCard>
            </Section>
          </Body>
        </Panel>
      </Overlay>

      {/* EditMemberModal is rendered outside the Overlay so its backdrop clicks
          do not propagate to the drawer's onClose. It receives zIndex={nestedZIndex}
          (popover=1060) so it stacks above the drawer Panel (modal=1050). */}
      <EditMemberModal
        member={member}
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        zIndex={nestedZIndex}
      />

      {showReactivate && (
        <ReactivateMemberModal
          member={member}
          isOpen={showReactivate}
          onClose={() => setShowReactivate(false)}
          onReactivated={(m, result) =>
            showSuccess(
              result.signInEnabled
                ? `${m.firstName} has been reactivated and their sign-in re-enabled.`
                : `${m.firstName} has been reactivated.`,
            )
          }
          zIndex={nestedZIndex}
        />
      )}

      <TempPasswordModal
        isOpen={tempPassword !== null}
        onClose={handleTempPasswordClose}
        temporaryPassword={tempPassword}
        userLabel={tempPasswordLabel}
      />
    </>
  );
}

export default TeamMemberDetailDrawer;
